import { zodResolver } from '@hookform/resolvers/zod';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardDoubleArrowDown from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUp from '@mui/icons-material/KeyboardDoubleArrowUp';
import { Box, Button, CircularProgress, InputAdornment, TextField, Tooltip } from '@mui/material';
import { green, orange, red } from '@mui/material/colors';
import Typography from '@mui/material/Typography';
import { endOfDay } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useTheme as useStyledTheme } from 'styled-components/macro';

import MuiFormDatePicker from 'components/Form/MuiFormDatePicker/MuiFormDatePicker';
import MuiFormInput from 'components/Form/MuiFormInput/MuiFormInput';
import MuiFormSelect from 'components/Form/MuiFormSelect/MuiFormSelect';
import type { MuiFormSelectOptionT } from 'components/Form/MuiFormSelect/MuiFormSelect';
import Popup from 'components/Popup/Popup';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';

import {
  encodeCombinedCategorySubcategoryValue,
  parseCombinedCategorySubcategoryValue,
} from './categorySubcategoryOptions';
import type { NewTicketFormValuesT } from './declarations';
import useOpenNewTicketPopup from './hooks/useOpenNewTicketPopup';
import {
  FormStyled,
  NewTicketPopupContentWrapStyled,
  NewTicketPopupScrollWrapStyled,
  getPopupContentStyle,
} from './OpenNewTicketPopup.styled';
import validationSchema from './validationSchema';

const OpenNewTicketPopup = (): JSX.Element => {
  const {
    combinedCategorySubcategoryOptions,
    isLoadingCombinedCategorySubcategoryOptions,
    claimStatusesOptions,
    roofTopIdsOptions,
    isLoadingRoofTopOptions,
    ticketPrioritiesOptions,
    isLoadingTicketPrioritiesOptions,
    handleCloseNewTicketPopup,
    handleSubmitForm,
    handleAmountBlur,
    isPopupOpened,
    handleRoofTopChange,
    ticketRequestCategories,
    subcategoriesByCategoryId,
    isLoadingRequestCategoriesOptions,
    handleLineNumberChange,
    handleROChange,
    lineItemsObj,
    setAdvisorNumber,
    setRecentRoData,
    checkIfRoExists,
    isCreatingNewTicket,
    isLoadingClaimStatusesOptions,
    setShowLineNumberDropdown,
    showLineNumberDropdown,
    lineNumbers,
    setLineNumbers,
    setSelectedRoofTopId,
  } = useOpenNewTicketPopup();

  const [techNumber, setTechNumber] = useState<string | undefined>(undefined);
  const [singleTicketMessage, setSingleTicketMessage] = useState<string>('');

  const { isMobile, isTablet, isDesktop, isLargeDesktop } = useResponsive();
  const styledTheme = useStyledTheme();

  const fromMethods = useForm<NewTicketFormValuesT>({
    defaultValues: {
      lineNumber: null,
      amount: '0.00',
      text: '',
      subCategoryId: null,
      categorySubcategorySelection: '',
    },
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  const [searchParams] = useSearchParams();
  const { handleSubmit, setValue, reset, getValues, control, formState } = fromMethods;
  const datePickerMaxDate = endOfDay(new Date());
  const selectedRoofTopId = searchParams.get(TicketsRefinementKeysE.RoofTopId);

  const watchedRoofTopId = useWatch({ control, name: 'roofTopId', defaultValue: '' });
  const watchedLineNumber = useWatch({ control, name: 'lineNumber', defaultValue: null });
  const watchedRoNumber = useWatch({ control, name: 'roNumber', defaultValue: '' });
  const ticketPrioritiesOptionsWithIcon: MuiFormSelectOptionT[] = useMemo(() => {
    const labelToIconAndColor: Record<
      string,
      { icon: React.ReactNode; sxProps: { color: string } }
    > = {
      Critical: { icon: <KeyboardDoubleArrowUp key="critical" />, sxProps: { color: red[900] } },
      High: { icon: <KeyboardArrowUp key="high" />, sxProps: { color: red[500] } },
      Medium: { icon: <KeyboardArrowDown key="medium" />, sxProps: { color: orange[500] } },
      Low: { icon: <KeyboardDoubleArrowDown key="low" />, sxProps: { color: green[500] } },
    };
    return ticketPrioritiesOptions.map(opt => {
      const mapped = labelToIconAndColor[opt.label];
      return {
        value: opt.value,
        label: opt.label,
        icon: mapped?.icon ?? opt.label,
        sxProps: mapped?.sxProps,
      };
    });
  }, [ticketPrioritiesOptions]);

  useEffect((): void => {
    if (isPopupOpened && getValues('roofTopId') === undefined && selectedRoofTopId !== null) {
      setValue('roofTopId', selectedRoofTopId);
      setSelectedRoofTopId(selectedRoofTopId);
    }
  }, [isPopupOpened, selectedRoofTopId, getValues, setValue, setSelectedRoofTopId]);

  useEffect((): void => {
    if (isPopupOpened && getValues('ticketPriorityId') === undefined) {
      setValue('ticketPriorityId', 3);
    }
  }, [isPopupOpened, getValues, setValue]);

  useEffect((): void => {
    if (watchedRoofTopId && typeof watchedRoofTopId === 'string') {
      handleRoofTopChange(watchedRoofTopId);
    }
  }, [watchedRoofTopId, handleRoofTopChange]);

  useEffect((): void => {
    if (watchedLineNumber != null) {
      const lineVal =
        typeof watchedLineNumber === 'object' &&
        watchedLineNumber !== null &&
        'value' in (watchedLineNumber as object)
          ? (watchedLineNumber as { value: string }).value
          : String(watchedLineNumber);
      handleLineNumberChange(lineVal);
      const selectedLineItem = lineItemsObj.find((item): boolean => item.lineNumber === lineVal);
      setTechNumber(selectedLineItem?.techNumber);
    }
  }, [watchedLineNumber, handleLineNumberChange, lineItemsObj]);

  useEffect((): void => {
    if (watchedRoNumber != null && watchedRoNumber !== '') {
      handleROChange(String(watchedRoNumber));
    }
  }, [watchedRoNumber, handleROChange]);

  useEffect((): void => {
    setValue('techNumber', techNumber ?? '');
  }, [techNumber, setValue]);

  useEffect((): void => {
    if (isPopupOpened && roofTopIdsOptions.length === 1) {
      const id = roofTopIdsOptions[0].value as string;
      setSelectedRoofTopId(id);
      setValue('roofTopId', id);
    }
    if (isPopupOpened && lineNumbers.length === 1) {
      const lineNumber = lineNumbers[0].value as string;
      setValue('lineNumber', showLineNumberDropdown ? lineNumber : null);
      setValue('techNumber', lineItemsObj[0]?.techNumber ?? '');
    }
  }, [
    roofTopIdsOptions,
    isPopupOpened,
    setSelectedRoofTopId,
    setValue,
    lineItemsObj,
    lineNumbers,
    showLineNumberDropdown,
    handleLineNumberChange,
  ]);

  useEffect(
    (): (() => void) => (): void => {
      if (isPopupOpened) {
        reset();
        setSingleTicketMessage('');
      }
    },
    [isPopupOpened, reset]
  );

  useEffect((): void => {
    if (!isPopupOpened) {
      setShowLineNumberDropdown(false);
      setTechNumber('');
      setLineNumbers([]);
      setAdvisorNumber(undefined);
      setRecentRoData({ roofTopId: null, roNumber: null });
      if (!selectedRoofTopId) {
        setSelectedRoofTopId(undefined);
      }
    }
  }, [
    isPopupOpened,
    selectedRoofTopId,
    setAdvisorNumber,
    setLineNumbers,
    setRecentRoData,
    setShowLineNumberDropdown,
    setSelectedRoofTopId,
  ]);

  const handleRoNumberBlur = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const roNumber = event.target.value;
    const { roofTopId } = getValues();
    handleROChange(roNumber);
    checkIfRoExists(roofTopId, roNumber, setValue);
  };

  const handleCombinedCategorySubcategoryChange = (value: string | number): void => {
    const parsed = parseCombinedCategorySubcategoryValue(value);
    if (!parsed) {
      setValue('requestCategoryId', undefined as unknown as number);
      setValue('requestSubcategoryId', null);
      setValue('requestSubCategoryName', null);
      setValue('categorySubcategorySelection', '');
      return;
    }

    const { categoryId, subcategoryId } = parsed;
    setValue('requestCategoryId', categoryId);
    setValue('requestSubcategoryId', subcategoryId);
    setValue(
      'categorySubcategorySelection',
      encodeCombinedCategorySubcategoryValue(categoryId, subcategoryId)
    );

    const selectedCategory = ticketRequestCategories?.find(
      (category): boolean => category.id === categoryId
    );
    const selectedSubcategory = subcategoriesByCategoryId[categoryId]?.find(
      (sub): boolean => sub.id === subcategoryId
    );

    setValue('requestSubCategoryName', selectedSubcategory?.name ?? null);

    const message =
      (selectedSubcategory as { description?: string })?.description ||
      (selectedCategory as { defaultMessage?: string })?.defaultMessage ||
      '';
    setValue('text', message);
    setSingleTicketMessage(message);
  };

  const handleChangeCategorySingle = (): void => {
    const textFieldValue = getValues('text');
    if (!textFieldValue || textFieldValue === '') {
      const categoryId = getValues('requestCategoryId');
      const foundCategory = ticketRequestCategories?.find(
        (category): boolean => category.id === categoryId
      );
      setSingleTicketMessage((foundCategory as { defaultMessage?: string })?.defaultMessage || '');
    }
  };

  return (
    <Popup
      showCloseBtn
      modalTitle="New Ticket"
      popupId={ScrollLockIdE.OpenNewTicketPopup}
      contentStyle={getPopupContentStyle(
        styledTheme,
        isMobile,
        isTablet,
        isDesktop,
        isLargeDesktop
      )}
      isOpen={isPopupOpened}
      onClose={handleCloseNewTicketPopup}
      closeOnEscape={false}
      closeOnDocumentClick
      width={463}
      height={735}
      validationErrorCount={Object.keys(formState.errors).length}
    >
      <NewTicketPopupContentWrapStyled direction="column" align="stretch">
        <FormProvider {...fromMethods}>
          <FormStyled role="form" onSubmit={handleSubmit(handleSubmitForm)}>
            <NewTicketPopupScrollWrapStyled>
              <Box display="flex" flexDirection="column" gap={2}>
                {/* Dealer */}
                <Typography variant="subtitle2" fontWeight={700}>
                  Dealer
                </Typography>
                <MuiFormSelect
                  required
                  searchable
                  label="Dealer"
                  name="roofTopId"
                  placeholder=""
                  options={roofTopIdsOptions}
                  isLoading={isLoadingRoofTopOptions}
                />

                {/* RO Info */}
                <Typography variant="subtitle2" fontWeight={700}>
                  RO Info
                </Typography>
                <Box display="flex" gap={2}>
                  <Box flex="1">
                    <MuiFormInput
                      required
                      label="RO Number"
                      name="roNumber"
                      placeholder=""
                      onBlur={handleRoNumberBlur}
                    />
                  </Box>
                  <Box flex="1">
                    {showLineNumberDropdown ? (
                      <MuiFormSelect
                        label="Line Number"
                        name="lineNumber"
                        placeholder=""
                        options={lineNumbers}
                      />
                    ) : (
                      <MuiFormInput label="Line Number" name="lineNumber" placeholder="" />
                    )}
                  </Box>
                </Box>

                {/* Open RO Date and Amount */}
                <Box display="flex" gap={2}>
                  <Box flex="1">
                    <MuiFormDatePicker
                      required
                      label="Open RO Date"
                      name="roDate"
                      placeholder="Select date"
                      maxDate={datePickerMaxDate}
                    />
                  </Box>
                  <Box flex="1">
                    <MuiFormInput
                      label="Amount"
                      name="amount"
                      placeholder="0.00"
                      type="number"
                      onBlur={handleAmountBlur(setValue)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoneyIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>

                {/* Advisor, Tech and VIN */}
                <Box display="flex" gap={2}>
                  <Box flex="1">
                    <MuiFormInput
                      required
                      label="Advisor"
                      placeholder="Enter advisor"
                      name="advisorNumber"
                    />
                  </Box>
                  <Box flex="1">
                    <MuiFormInput
                      required
                      label="Tech"
                      name="techNumber"
                      placeholder="Enter tech"
                    />
                  </Box>
                  <Box flex="1">
                    <MuiFormInput label="VIN" placeholder="VIN" name="vin" />
                  </Box>
                </Box>

                {/* Claim Status */}
                <Typography variant="subtitle2" fontWeight={700}>
                  Claim Status
                </Typography>
                <Box display="flex" gap={2}>
                  <Box flex="1">
                    <MuiFormSelect
                      required
                      label="Claim Status"
                      name="claimStatusId"
                      placeholder=""
                      options={claimStatusesOptions}
                      isLoading={isLoadingClaimStatusesOptions}
                    />
                  </Box>
                  <Box flex="1">
                    <MuiFormDatePicker
                      label="Claim Submitted Date"
                      name="claimSubmittedDate"
                      placeholder=""
                      maxDate={datePickerMaxDate}
                    />
                  </Box>
                </Box>

                {/* Ticket Setup */}
                <Typography variant="subtitle2" fontWeight={700}>
                  Ticket Setup
                </Typography>
                <Box display="flex" gap={2} alignItems="flex-start">
                  <Box flex={1} minWidth={0}>
                    <MuiFormSelect
                      required
                      label="Priority"
                      name="ticketPriorityId"
                      placeholder=""
                      options={ticketPrioritiesOptionsWithIcon}
                      isLoading={isLoadingTicketPrioritiesOptions}
                      renderSelectedValue={(opt): React.ReactNode => (
                        <Box display="flex" alignItems="center" sx={opt.sxProps ?? undefined}>
                          {opt.icon}
                        </Box>
                      )}
                      sx={{
                        '& .MuiSelect-select': {
                          paddingRight: '32px !important',
                          paddingLeft: '14px !important',
                          paddingTop: '6px !important',
                          paddingBottom: '6px !important',
                          display: 'flex',
                          alignItems: 'center',
                        },
                        '& .MuiOutlinedInput-root': { height: 'auto' },
                        '& .MuiInputLabel-shrink': {
                          transform: 'translate(14px, -6px) scale(0.75)',
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Category */}
                <Tooltip title={!watchedRoofTopId ? 'Select a dealer first' : ''}>
                  <Box component="span" sx={{ display: 'inline-block', width: '100%' }}>
                    <MuiFormSelect
                      required
                      label="Category"
                      name="categorySubcategorySelection"
                      placeholder=""
                      options={combinedCategorySubcategoryOptions}
                      isLoading={
                        isLoadingRequestCategoriesOptions ||
                        isLoadingCombinedCategorySubcategoryOptions
                      }
                      disabled={!watchedRoofTopId}
                      searchable
                      onChange={(value): void => {
                        handleCombinedCategorySubcategoryChange(value);
                      }}
                    />
                  </Box>
                </Tooltip>

                {/* Message */}
                <Controller
                  name="text"
                  control={fromMethods.control}
                  render={({ field }): JSX.Element => (
                    <TextField
                      {...field}
                      multiline
                      minRows={1}
                      placeholder="Enter message"
                      fullWidth
                      size="small"
                      value={field.value ?? singleTicketMessage ?? ''}
                      onChange={(e): void => {
                        handleChangeCategorySingle();
                        setSingleTicketMessage(e.target.value);
                        field.onChange(e);
                      }}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: '13px',
                          overflow: 'hidden',
                          resize: 'none',
                        },
                        '& .MuiFormHelperText-root': { fontSize: '11px' },
                        '& .MuiInputBase-root': {
                          paddingTop: '8px',
                          alignItems: 'flex-start',
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </NewTicketPopupScrollWrapStyled>
            <Box
              display="flex"
              justifyContent="flex-end"
              mt={-2}
              px={2}
              pt={1}
              pb={2}
              flexShrink={0}
            >
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isCreatingNewTicket}
                startIcon={isCreatingNewTicket ? <CircularProgress size={16} /> : null}
              >
                Create
              </Button>
            </Box>
          </FormStyled>
        </FormProvider>
      </NewTicketPopupContentWrapStyled>
    </Popup>
  );
};

export default OpenNewTicketPopup;
